import type { DatasetRecord, Finding } from "../contracts/types";
import type { CoverageSummary } from "./coverage";
import type { FieldProfile } from "./profile";
import { toCsv } from "../lib/csv";
import { LIMITS } from "./limits";

export interface ExportResult {
  filename: string;
  mimeType: string;
  content: string;
  truncated: boolean;
}

function truncate(content: string): { content: string; truncated: boolean } {
  const truncated = content.length > LIMITS.maxExportBytes;
  return { content: truncated ? content.slice(0, LIMITS.maxExportBytes) : content, truncated };
}

/**
 * Exporta el dataset YA enmascarado (P13-R2/P13-R3). Nunca recibe el dataset
 * original: la UI solo puede invocar esto sobre `masked records`, así una
 * exportación no puede filtrar accidentalmente valores sin transformar de un
 * campo configurado. Usa `Content-Disposition: attachment` equivalente en
 * navegador (atributo `download`, ver src/lib/download.ts).
 */
export function exportDataset(
  runId: string,
  maskedRecords: DatasetRecord[],
  fieldOrder: string[],
  format: "json" | "csv",
): ExportResult {
  const content =
    format === "json" ? JSON.stringify(maskedRecords, null, 2) : toCsv(maskedRecords, fieldOrder);
  const { content: finalContent, truncated } = truncate(content);
  return {
    filename: `${runId}.masked.${format}`,
    mimeType: format === "json" ? "application/json" : "text/csv",
    content: finalContent,
    truncated,
  };
}

function severityLabel(severity: string): string {
  const labels: Record<string, string> = { info: "Info", warning: "Advertencia", error: "Error", critical: "Crítico" };
  return labels[severity] ?? severity;
}

/**
 * Genera el informe de cobertura exportable (P13-R4): qué campos se
 * detectaron como candidatos, qué regla se aplicó y qué queda sin cubrir.
 * Incluye siempre el descargo de responsabilidad sobre reidentificación —
 * nunca se omite, ni siquiera cuando la cobertura es del 100%.
 */
export function exportCoverageReport(
  runId: string,
  scenarioId: string,
  fieldProfiles: FieldProfile[],
  coverage: CoverageSummary,
  findings: Finding[],
  format: "markdown" | "json",
): ExportResult {
  if (format === "json") {
    const payload = {
      runId,
      scenarioId,
      generatedAt: new Date().toISOString(),
      fieldProfiles,
      coverage,
      findings,
      disclaimer:
        "Esta herramienta aplica transformaciones configurables sobre los datos. NO certifica anonimización real ni protege por sí sola frente a reidentificación por combinación de campos (k-anonimato, ataques de vinculación, etc.). Revisa el informe y valida con criterio experto antes de compartir datos.",
    };
    const { content, truncated } = truncate(JSON.stringify(payload, null, 2));
    return { filename: `${runId}.coverage.json`, mimeType: "application/json", content, truncated };
  }

  const lines: string[] = [];
  lines.push("# Data Anonymizer — informe de cobertura");
  lines.push("");
  lines.push(`- **Run:** \`${runId}\``);
  lines.push(`- **Escenario:** \`${scenarioId}\``);
  lines.push(
    `- **Cobertura de campos candidatos:** ${coverage.overall.percentage}% (${coverage.overall.covered}/${coverage.overall.relevant})`,
  );
  lines.push(`- **Generado:** ${new Date().toISOString()}`);
  lines.push("");
  lines.push(
    "> **Aviso:** esta herramienta aplica transformaciones configurables. NO certifica anonimización real " +
      "ni protege por sí sola frente a reidentificación por combinación de campos. Revisa este informe con " +
      "criterio experto antes de compartir cualquier dataset derivado.",
  );
  lines.push("");

  if (coverage.combinationRisk) {
    lines.push(
      `> **Riesgo de combinación:** los campos ${coverage.combinationRiskFields.map((f) => `\`${f}\``).join(", ")} ` +
        "quedan sin enmascarar a la vez y podrían permitir reidentificar filas al combinarlos entre sí.",
    );
    lines.push("");
  }

  lines.push("## Campos detectados");
  lines.push("");
  lines.push("| Campo | Tipo | Categoría | Candidato PII | Confianza | Estrategia | Cubierto |");
  lines.push("|---|---|---|---|---|---|---|");
  for (const profile of fieldProfiles) {
    const fieldCoverage = coverage.perField.find((entry) => entry.field === profile.name);
    lines.push(
      `| ${profile.name} | ${profile.inferredType} | ${profile.category} | ${profile.piiCandidate ? "sí" : "no"} | ${profile.confidence} | ${fieldCoverage?.strategy ?? "preserve"} | ${fieldCoverage?.covered ? "sí" : "no"} |`,
    );
  }
  lines.push("");

  if (coverage.uncoveredFields.length > 0) {
    lines.push(`## Campos candidatos sin cubrir (${coverage.uncoveredFields.length})`);
    lines.push("");
    for (const field of coverage.uncoveredFields) {
      lines.push(`- \`${field}\``);
    }
    lines.push("");
  }

  lines.push("## Hallazgos");
  lines.push("");
  for (const finding of findings) {
    lines.push(`- **[${severityLabel(finding.severity)}] ${finding.ruleId}:** ${finding.message}`);
  }

  const { content, truncated } = truncate(lines.join("\n"));
  return { filename: `${runId}.coverage.md`, mimeType: "text/markdown", content, truncated };
}
